'use client'

import { cn } from '@/lib/utils'
import { Button } from '@tailark/core/ui/button'
import { Input } from '@tailark/core/ui/input'
import { ScrollArea } from '@tailark/core/ui/scroll-area'
import { Check, FolderTree, LayoutGrid, Search, SlidersHorizontal, Tag, X, type LucideIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
    categoryAccentFa,
    categoryIconFa,
    categoryLabelFa,
    tagIconFa,
    tagLabelFa,
    templateKindAccentFa,
    templateKindIconFa,
    templateKindLabelFa,
    templatesPageCopy,
} from '../templates-copy-fa'
import { TEMPLATE_KINDS, type Template, type TemplateKind } from '../types'
import { TemplateCard } from './template-card'
import { TemplatePreviewModal } from './template-preview-modal'

interface TemplatesBrowserProps {
    templates: Template[]
}

export function TemplatesBrowser({ templates }: TemplatesBrowserProps) {
    const [query, setQuery] = useState('')
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
    const [selectedKinds, setSelectedKinds] = useState<Set<TemplateKind>>(new Set())
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
    const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null)

    const categoryCounts = useMemo(() => {
        const counts = new Map<string, number>()
        for (const t of templates) counts.set(t.category, (counts.get(t.category) ?? 0) + 1)
        return counts
    }, [templates])

    const allCategories = useMemo(
        () => Array.from(categoryCounts.keys()).sort((a, b) => (categoryCounts.get(b) ?? 0) - (categoryCounts.get(a) ?? 0) || a.localeCompare(b)),
        [categoryCounts]
    )

    const kindCounts = useMemo(() => {
        const counts = new Map<TemplateKind, number>()
        for (const t of templates) counts.set(t.templateKind, (counts.get(t.templateKind) ?? 0) + 1)
        return counts
    }, [templates])

    const tagCounts = useMemo(() => {
        const counts = new Map<string, number>()
        for (const t of templates) {
            for (const tag of t.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1)
        }
        return counts
    }, [templates])

    const allTags = useMemo(
        () =>
            Array.from(tagCounts.entries())
                .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                .map(([tag]) => tag),
        [tagCounts]
    )

    const filteredTemplates = useMemo(() => {
        const q = query.trim().toLowerCase()
        return templates
            .filter((t) => !q || t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q))
            .filter((t) => selectedCategories.size === 0 || selectedCategories.has(t.category))
            .filter((t) => selectedKinds.size === 0 || selectedKinds.has(t.templateKind))
            .filter((t) => selectedTags.size === 0 || t.tags.some((tag) => selectedTags.has(tag)))
            .sort((a, b) => a.title.localeCompare(b.title))
    }, [templates, query, selectedCategories, selectedKinds, selectedTags])

    const toggleValue = <T extends string>(set: Set<T>, value: T) => {
        const next = new Set(set)
        if (next.has(value)) next.delete(value)
        else next.add(value)
        return next
    }

    const clearAllFilters = () => {
        setQuery('')
        setSelectedCategories(new Set())
        setSelectedKinds(new Set())
        setSelectedTags(new Set())
    }

    const activeFilterCount = selectedCategories.size + selectedKinds.size + selectedTags.size + (query ? 1 : 0)

    return (
        <>
            <div className="grid gap-6 lg:grid-cols-[1fr_3fr] lg:gap-8">
                <aside className="lg:sticky lg:top-24 lg:self-start lg:pe-1">
                    <div className="bg-card overflow-hidden rounded-xl border">
                        <div className="relative flex shrink-0 items-center justify-between border-b bg-gradient-to-l to-transparent px-4 py-3">
                            <div className="flex items-center gap-2.5">
                                <div className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg shadow-sm">
                                    <SlidersHorizontal className="size-4" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-semibold leading-tight">{templatesPageCopy.filtersTitle}</h2>
                                    <p className="text-muted-foreground text-[10px] leading-tight">
                                        {activeFilterCount > 0
                                            ? `${activeFilterCount.toLocaleString('fa-IR')} فیلتر فعال`
                                            : 'بدون فیلتر'}
                                    </p>
                                </div>
                            </div>
                            {activeFilterCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearAllFilters}
                                    className="text-muted-foreground hover:text-foreground h-7 gap-1 px-2 text-xs">
                                    <X className="size-3.5" />
                                    {templatesPageCopy.clearFilters}
                                </Button>
                            )}
                        </div>

                        <ScrollArea className="h-[calc(100vh-19rem)] max-h-[calc(100vh-19rem)] w-full">
                            <div className="divide-border divide-y" dir="rtl">
                                <FilterSection
                                    icon={LayoutGrid}
                                    title={templatesPageCopy.filterTemplateType}
                                    activeCount={selectedKinds.size}>
                                    <IconFilterList
                                        options={TEMPLATE_KINDS}
                                        selected={selectedKinds}
                                        getLabel={(value) => templateKindLabelFa(value)}
                                        getIcon={(value) => templateKindIconFa(value)}
                                        getAccent={(value) => templateKindAccentFa(value)}
                                        getCount={(value) => kindCounts.get(value) ?? 0}
                                        onToggle={(value) => setSelectedKinds((prev) => toggleValue(prev, value))}
                                    />
                                </FilterSection>

                                <FilterSection
                                    icon={FolderTree}
                                    title={templatesPageCopy.filterCategories}
                                    activeCount={selectedCategories.size}>
                                    <IconFilterList
                                        options={allCategories}
                                        selected={selectedCategories}
                                        getLabel={(value) => categoryLabelFa(value)}
                                        getIcon={(value) => categoryIconFa(value)}
                                        getAccent={(value) => categoryAccentFa(value)}
                                        getCount={(value) => categoryCounts.get(value) ?? 0}
                                        onToggle={(value) => setSelectedCategories((prev) => toggleValue(prev, value))}
                                    />
                                </FilterSection>

                                {allTags.length > 0 && (
                                    <FilterSection
                                        icon={Tag}
                                        title={templatesPageCopy.filterTags}
                                        activeCount={selectedTags.size}>
                                        <IconChipList
                                            options={allTags}
                                            selected={selectedTags}
                                            getLabel={(value) => tagLabelFa(value)}
                                            getIcon={(value) => tagIconFa(value)}
                                            onToggle={(value) => setSelectedTags((prev) => toggleValue(prev, value))}
                                        />
                                    </FilterSection>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </aside>

                <main className="min-w-0">
                    <div className="relative mb-4">
                        <Search
                            aria-hidden
                            className="text-muted-foreground pointer-events-none absolute start-3.5 top-1/2 size-4 -translate-y-1/2"
                        />
                        <Input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={templatesPageCopy.searchPlaceholder}
                            aria-label={templatesPageCopy.searchAriaLabel}
                            className="bg-card border-border/80 focus-visible:border-primary h-12 rounded-lg pe-10 ps-10 text-sm shadow-sm"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => setQuery('')}
                                aria-label={templatesPageCopy.clearFilters}
                                className="text-muted-foreground hover:bg-muted hover:text-foreground absolute end-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors">
                                <X className="size-4" />
                            </button>
                        )}
                    </div>

                    {activeFilterCount > 0 && (
                        <ActiveFiltersBar
                            query={query}
                            categories={selectedCategories}
                            kinds={selectedKinds}
                            tags={selectedTags}
                            onClearQuery={() => setQuery('')}
                            onRemoveCategory={(value) => setSelectedCategories((prev) => toggleValue(prev, value))}
                            onRemoveKind={(value) => setSelectedKinds((prev) => toggleValue(prev, value))}
                            onRemoveTag={(value) => setSelectedTags((prev) => toggleValue(prev, value))}
                            onClearAll={clearAllFilters}
                        />
                    )}

                    {filteredTemplates.length === 0 ? (
                        <EmptyState onClear={clearAllFilters} />
                    ) : (
                        <div className="grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2 xl:grid-cols-3">
                            {filteredTemplates.map((template) => (
                                <TemplateCard
                                    key={template.id}
                                    template={template}
                                    onClick={() => setPreviewTemplate(template)}
                                />
                            ))}
                        </div>
                    )}
                </main>
            </div>

            <TemplatePreviewModal
                template={previewTemplate}
                isOpen={previewTemplate !== null}
                onClose={() => setPreviewTemplate(null)}
            />
        </>
    )
}

interface FilterSectionProps {
    icon: LucideIcon
    title: string
    activeCount: number
    children: React.ReactNode
}

function FilterSection({ icon: Icon, title, activeCount, children }: FilterSectionProps) {
    return (
        <section className="px-3 py-3">
            <header className="mb-2.5 flex items-center gap-2 px-1">
                <Icon className="text-muted-foreground size-3.5" />
                <h3 className="text-foreground/80 text-[11px] font-semibold uppercase tracking-wider">{title}</h3>
                {activeCount > 0 && (
                    <span className="bg-primary/10 text-primary ms-auto inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold">
                        {activeCount.toLocaleString('fa-IR')}
                    </span>
                )}
            </header>
            {children}
        </section>
    )
}

interface IconFilterListProps<T extends string> {
    options: readonly T[]
    selected: Set<T>
    getLabel: (value: T) => string
    getIcon: (value: T) => LucideIcon
    getAccent: (value: T) => string
    getCount: (value: T) => number
    onToggle: (value: T) => void
}

function IconFilterList<T extends string>({ options, selected, getLabel, getIcon, getAccent, getCount, onToggle }: IconFilterListProps<T>) {
    return (
        <ul role="list" className="px-1">
            {options.map((option) => {
                const isActive = selected.has(option)
                const Icon = getIcon(option)
                const count = getCount(option)
                const accent = getAccent(option)
                return (
                    <li key={option} className="mb-1.5 me-1.5 inline-block align-top last:me-0">
                        <button
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => onToggle(option)}
                            className={cn(
                                'group inline-flex max-w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-start text-sm transition-all',
                                isActive
                                    ? 'bg-primary/10 ring-primary/20 text-primary ring-1'
                                    : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                            )}>
                            <span
                                className={cn(
                                    'flex size-7 shrink-0 items-center justify-center rounded-md transition-all',
                                    isActive ? 'bg-primary text-primary-foreground shadow-sm' : accent
                                )}>
                                {isActive ? <Check className="size-3.5" /> : <Icon className="size-3.5" />}
                            </span>
                            <span className={cn('flex-1 truncate text-xs', isActive && 'font-medium')}>{getLabel(option)}</span>
                            <span
                                className={cn(
                                    'rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums transition-colors',
                                    isActive ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground group-hover:bg-background'
                                )}>
                                {count.toLocaleString('fa-IR')}
                            </span>
                        </button>
                    </li>
                )
            })}
        </ul>
    )
}

interface IconChipListProps<T extends string> {
    options: readonly T[]
    selected: Set<T>
    getLabel: (value: T) => string
    getIcon: (value: T) => LucideIcon
    onToggle: (value: T) => void
}

function IconChipList<T extends string>({ options, selected, getLabel, getIcon, onToggle }: IconChipListProps<T>) {
    return (
        <div className="flex flex-wrap gap-1.5 px-1">
            {options.map((option) => {
                const isActive = selected.has(option)
                const Icon = getIcon(option)
                return (
                    <button
                        key={option}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => onToggle(option)}
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-all',
                            isActive
                                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                                : 'border-border bg-background text-foreground/80 hover:border-foreground/30 hover:bg-muted hover:text-foreground'
                        )}>
                        <Icon className="size-3" />
                        <span>{getLabel(option)}</span>
                    </button>
                )
            })}
        </div>
    )
}

interface ActiveFiltersBarProps {
    query: string
    categories: Set<string>
    kinds: Set<TemplateKind>
    tags: Set<string>
    onClearQuery: () => void
    onRemoveCategory: (value: string) => void
    onRemoveKind: (value: TemplateKind) => void
    onRemoveTag: (value: string) => void
    onClearAll: () => void
}

function ActiveFiltersBar({ query, categories, kinds, tags, onClearQuery, onRemoveCategory, onRemoveKind, onRemoveTag, onClearAll }: ActiveFiltersBarProps) {
    return (
        <div className="bg-muted/40 mb-4 flex flex-wrap items-center gap-1.5 rounded-lg border border-dashed p-2">
            {query && (
                <ActiveFilterChip
                    icon={Search}
                    label={`"${query}"`}
                    onRemove={onClearQuery}
                />
            )}
            {Array.from(kinds).map((kind) => (
                <ActiveFilterChip
                    key={`kind-${kind}`}
                    icon={templateKindIconFa(kind)}
                    label={templateKindLabelFa(kind)}
                    onRemove={() => onRemoveKind(kind)}
                />
            ))}
            {Array.from(categories).map((category) => (
                <ActiveFilterChip
                    key={`cat-${category}`}
                    icon={categoryIconFa(category)}
                    label={categoryLabelFa(category)}
                    onRemove={() => onRemoveCategory(category)}
                />
            ))}
            {Array.from(tags).map((tag) => (
                <ActiveFilterChip
                    key={`tag-${tag}`}
                    icon={tagIconFa(tag)}
                    label={tagLabelFa(tag)}
                    onRemove={() => onRemoveTag(tag)}
                />
            ))}
            <button
                type="button"
                onClick={onClearAll}
                className="text-muted-foreground hover:text-foreground ms-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition-colors hover:underline">
                <X className="size-3" />
                {templatesPageCopy.emptyClearButton}
            </button>
        </div>
    )
}

function ActiveFilterChip({ icon: Icon, label, onRemove }: { icon: LucideIcon; label: string; onRemove: () => void }) {
    return (
        <span className="bg-background inline-flex items-center gap-1 rounded-full border py-0.5 pe-0.5 ps-2 text-[11px] shadow-sm">
            <Icon className="text-muted-foreground size-3" />
            <span className="max-w-[140px] truncate">{label}</span>
            <button
                type="button"
                onClick={onRemove}
                aria-label={`${templatesPageCopy.clearFilters}: ${label}`}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-4 items-center justify-center rounded-full transition-colors">
                <X className="size-2.5" />
            </button>
        </span>
    )
}

function EmptyState({ onClear }: { onClear: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <p className="text-base font-medium">{templatesPageCopy.emptyTitle}</p>
            <p className="text-muted-foreground mt-1 text-sm">{templatesPageCopy.emptyHint}</p>
            <Button
                variant="outline"
                size="sm"
                onClick={onClear}
                className="mt-4">
                {templatesPageCopy.emptyClearButton}
            </Button>
        </div>
    )
}
